import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import {
	Controller,
	type ControllerProps,
	type FieldPath,
	type FieldValues,
	FormProvider,
	type SubmitHandler,
	type UseFormReturn,
	useFormContext,
	useFormState,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Label } from "@/components/ui/label";
import { markFormClean, markFormDirty } from "@/lib/pwa/dirtyFormRegistry";
import { resolveFormServerError } from "@/utils/apiErrorMessage";
import { cn } from "@/utils/utils";

/**
 * RHF-context field primitives for the forms standard (§3.2). Every field
 * renders the same skeleton:
 *
 *   <FormField name="…" control={control} render={({ field }) => (
 *     <FormItem>
 *       <FormLabel required>…</FormLabel>
 *       <FormControl>…input/select/…</FormControl>
 *       <FormDescription>…</FormDescription>   ← helper text (optional)
 *       <FormMessage />                          ← inline error
 *     </FormItem>
 *   )} />
 *
 * Context-aware primitives (`FormMessage`, `FormRootMessage`, `SubmitButton`)
 * require the form to be mounted under the `Form` provider.
 */

/**
 * Publishes "this form is mid-edit" to the PWA update policy so a service-worker
 * reload never lands on top of unsaved typing. Registration is automatic —
 * ~57 forms mount under `Form`, and an opt-in hook would leave the policy
 * permissive for every form that forgot to call it (and for forms not yet
 * written).
 *
 * Only the boolean dirtiness is published, keyed by an opaque `useId()`. No
 * field name and no field value ever reaches the PWA layer — form content here
 * is LGPD-sensitive (CPF, salary).
 *
 * The cleanup releases the key on unmount, so the registry's set is bounded by
 * mounted forms rather than by session length. Both paths are idempotent, which
 * is what makes the StrictMode double-invoke a no-op.
 */
function useDirtyFormRegistration(isDirty: boolean): void {
	const formId = React.useId();

	React.useEffect(() => {
		if (isDirty) {
			markFormDirty(formId);
		} else {
			markFormClean(formId);
		}

		return () => {
			markFormClean(formId);
		};
	}, [formId, isDirty]);
}

/**
 * Decorates RHF's `FormProvider` with dirty-form registration. Prop contract is
 * `FormProvider`'s, unchanged — every existing `<Form {...form}>` call site is
 * untouched.
 */
function Form<
	TFieldValues extends FieldValues,
	TContext = unknown,
	TTransformedValues = TFieldValues,
>(
	props: React.ComponentProps<
		typeof FormProvider<TFieldValues, TContext, TTransformedValues>
	>,
) {
	useDirtyFormRegistration(props.formState.isDirty);

	return <FormProvider {...props} />;
}

type FormFieldContextValue<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
	name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue | null>(
	null,
);

function FormField<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ ...props }: ControllerProps<TFieldValues, TName>) {
	return (
		<FormFieldContext.Provider value={{ name: props.name }}>
			<Controller {...props} />
		</FormFieldContext.Provider>
	);
}

type FormItemContextValue = {
	id: string;
};

const FormItemContext = React.createContext<FormItemContextValue | null>(null);

function useFormField() {
	const fieldContext = React.useContext(FormFieldContext);
	const itemContext = React.useContext(FormItemContext);
	const { getFieldState } = useFormContext();
	const formState = useFormState({ name: fieldContext?.name });

	if (!fieldContext) {
		throw new Error("useFormField should be used within <FormField>");
	}
	if (!itemContext) {
		throw new Error("useFormField should be used within <FormItem>");
	}

	const fieldState = getFieldState(fieldContext.name, formState);
	const { id } = itemContext;

	return {
		id,
		name: fieldContext.name,
		formItemId: `${id}-form-item`,
		formDescriptionId: `${id}-form-item-description`,
		formMessageId: `${id}-form-item-message`,
		...fieldState,
	};
}

function FormItem({ className, ...props }: React.ComponentProps<"div">) {
	const id = React.useId();

	return (
		<FormItemContext.Provider value={{ id }}>
			<div
				data-slot="form-item"
				// content-start: when a grid row pairs a short field with a taller one
				// (e.g. an input beside an input-with-chips), the stretched item must
				// keep label/control/message packed at the top instead of distributing
				// the extra height between them.
				className={cn("grid content-start gap-2", className)}
				{...props}
			/>
		</FormItemContext.Provider>
	);
}

function FormLabel({
	className,
	required,
	children,
	...props
}: React.ComponentProps<typeof Label> & {
	/** Marks the field as backend-required — renders the asterisk. */
	required?: boolean;
}) {
	const { error, formItemId } = useFormField();

	return (
		<Label
			data-slot="form-label"
			data-error={!!error}
			className={cn("data-[error=true]:text-error", className)}
			htmlFor={formItemId}
			{...props}
		>
			{children}
			{required && (
				<span aria-hidden="true" className="text-error -ml-1">
					*
				</span>
			)}
		</Label>
	);
}

function FormControl({ ...props }: React.ComponentProps<typeof Slot>) {
	const { error, formItemId, formDescriptionId, formMessageId } =
		useFormField();

	return (
		<Slot
			data-slot="form-control"
			id={formItemId}
			aria-describedby={
				error ? `${formDescriptionId} ${formMessageId}` : formDescriptionId
			}
			aria-invalid={!!error}
			{...props}
		/>
	);
}

function FormDescription({ className, ...props }: React.ComponentProps<"p">) {
	const { error, formDescriptionId } = useFormField();

	// The error replaces the helper text in the same slot (no layout jump).
	if (error) return null;

	return (
		<p
			data-slot="form-description"
			id={formDescriptionId}
			className={cn("text-sm text-base-content/70", className)}
			{...props}
		/>
	);
}

function FormMessage({ className, ...props }: React.ComponentProps<"p">) {
	const { error, formMessageId } = useFormField();
	const { t } = useTranslation();

	if (!error?.message) return null;

	// Schema messages are i18n keys (forms.validation.*); t() passes unknown
	// strings through unchanged, so raw messages still render.
	return (
		<p
			data-slot="form-message"
			id={formMessageId}
			role="alert"
			className={cn("text-sm text-error", className)}
			{...props}
		>
			{t(String(error.message))}
		</p>
	);
}

/**
 * Root server error (§3.5b) — rendered above the action bar/footer so an
 * authoritative backend rejection is visible in the form, not only in a toast.
 * Set by `useGuardedSubmit` on mutation rejection; cleared on next submit.
 */
function FormRootMessage({ className, ...props }: React.ComponentProps<"p">) {
	const { errors } = useFormState();
	const rootError = errors.root as
		| Record<string, { message?: string }>
		| undefined;
	const message = rootError?.server?.message;

	if (!message) return null;

	return (
		<p
			data-slot="form-root-message"
			role="alert"
			className={cn(
				"rounded-md border border-error/30 bg-error/10 px-3 py-2 text-sm text-error",
				className,
			)}
			{...props}
		>
			{message}
		</p>
	);
}

/**
 * Single-flight submit guard (§3.5, gate G3a). RHF's `handleSubmit` never
 * checks `isSubmitting` before re-invoking `onValid`, and a disabled button
 * lags a render — so two submit events in the same tick would double-fire the
 * mutation. The synchronous ref closes that window: set before the async work
 * starts, cleared in `finally`; re-entrant calls return immediately.
 *
 * `onValid` must return the mutation promise (`return mutateAsync(...)`) so
 * the flag truthfully spans the request. On rejection the error lands in
 * `root.server` (rendered by `FormRootMessage`) and is not rethrown — the
 * mutation hook's toast still fires independently.
 */
function useGuardedSubmit<
	TFieldValues extends FieldValues,
	TTransformed = TFieldValues,
>(
	form: UseFormReturn<TFieldValues, unknown, TTransformed>,
	onValid: SubmitHandler<TTransformed>,
): (e?: React.BaseSyntheticEvent) => Promise<void> {
	const { t, i18n } = useTranslation();
	const inFlightRef = React.useRef(false);
	const onValidRef = React.useRef(onValid);
	onValidRef.current = onValid;

	return React.useMemo(
		() =>
			form.handleSubmit(async (values, event) => {
				if (inFlightRef.current) return;
				inFlightRef.current = true;
				form.clearErrors("root.server" as FieldPath<TFieldValues>);
				try {
					await onValidRef.current(values, event);
				} catch (error) {
					form.setError("root.server" as FieldPath<TFieldValues>, {
						message: resolveFormServerError(error, t, i18n),
					});
				} finally {
					inFlightRef.current = false;
				}
			}),
		[form, t, i18n],
	);
}

export {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormRootMessage,
	useFormField,
	useGuardedSubmit,
};
